import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

/**
 * Custom hook for interacting with message endpoints for a specific workspace and thread
 * @param {string} workspaceId - The ID of the workspace
 * @param {string} threadId - The ID of the thread
 * @returns {Object} Object containing functions for message operations and their states
 */
const useMessages = (workspaceId, threadId) => {
  const authenticatedApi = useAuthenticatedApi();
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear messages when workspace or thread changes to prevent showing stale data
  useEffect(() => {
    setMessages([]);
    setCurrentMessage(null);
    setError(null);
  }, [workspaceId, threadId]);

  /**
   * Get the ID from a message object regardless of whether it uses id or _id
   * @param {Object} message - Message object
   * @returns {string|null} Message ID or null if not found
   */
  const getMessageId = (message) => {
    return message?.id || message?._id || null;
  };

  /**
   * Fetches all messages for the thread
   * @param {Object} options - Query options
   * @param {number} [options.limit] - Maximum number of messages to return
   * @param {number} [options.offset] - Number of messages to skip from the end
   * @param {boolean} [options.getAll=false] - If true, returns all messages
   * @param {boolean} [options.includeSubAgentMessages=false] - If true, includes sub-agent messages inline
   * @returns {Promise<Array|null>} Array of messages or null on error
   */
  const getMessages = async (options = {}) => {
    if (!workspaceId || !threadId) {
      setError(new Error('Workspace ID and Thread ID are required'));
      return null;
    }

    const { limit = 50, offset = 0, getAll = false, includeSubAgentMessages = false } = options;

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (getAll) {
        queryParams.append('get_all', 'true');
      } else {
        queryParams.append('limit', limit.toString());
        queryParams.append('offset', offset.toString());
      }
      
      if (includeSubAgentMessages) {
        queryParams.append('include_sub_agent_messages', 'true');
      }

      const endpoint = `${apiEndpoints.messages(workspaceId, threadId)}/?${queryParams.toString()}`;
      const data = await authenticatedApi.get(endpoint);
      
      // Transform data to ensure each message has an id property
      const normalizedData = data.map(message => ({
        ...message,
        id: getMessageId(message) // Ensure id is available regardless of _id vs id
      }));
      
      setMessages(normalizedData);
      setLoading(false);
      return normalizedData;
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  };

  /**
   * Fetches a specific message by ID
   * @param {string} messageId - ID of the message to fetch
   * @returns {Promise<Object|null>} Message object or null on error
   */
  const getMessage = async (messageId) => {
    if (!workspaceId || !threadId || !messageId) {
      setError(new Error('Workspace ID, Thread ID, and Message ID are required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = apiEndpoints.message(workspaceId, threadId, messageId);
      const data = await authenticatedApi.get(endpoint);
      
      // Ensure the message has an id property
      const normalizedMessage = {
        ...data,
        id: getMessageId(data)
      };
      
      setCurrentMessage(normalizedMessage);
      setLoading(false);
      return normalizedMessage;
    } catch (err) {
      console.error('Error fetching message:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  };

  /**
   * Creates a new message in the thread
   * @param {Object} messageData - Data for the new message
   * @param {string} messageData.role - The role of the message sender (user or assistant)
   * @param {Array} messageData.content - The content of the message as a list of dictionaries
   * @param {string} [messageData.stop_reason] - The reason why message generation stopped
   * @returns {Promise<Object|null>} Created message object or null on error
   */
  const createMessage = async (messageData) => {
    if (!workspaceId || !threadId) {
      setError(new Error('Workspace ID and Thread ID are required'));
      return null;
    }

    if (!messageData || !messageData.role || !messageData.content) {
      setError(new Error('Message role and content are required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await authenticatedApi.post(apiEndpoints.messages(workspaceId, threadId), messageData);
      
      // Ensure the message has an id property
      const normalizedMessage = {
        ...data,
        id: getMessageId(data)
      };
      
      console.log('Created message (normalized):', normalizedMessage);
      
      setCurrentMessage(normalizedMessage);
      setMessages(prevMessages => [...prevMessages, normalizedMessage]);
      setLoading(false);
      return normalizedMessage;
    } catch (err) {
      console.error('Error creating message:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  };

  /**
   * Updates an existing message
   * @param {string} messageId - ID of the message to update
   * @param {Object} updateData - Data to update the message with
   * @returns {Promise<Object|null>} Updated message object or null on error
   */
  const updateMessage = async (messageId, updateData) => {
    if (!workspaceId || !threadId || !messageId) {
      setError(new Error('Workspace ID, Thread ID, and Message ID are required'));
      return null;
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      setError(new Error('Update data is required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = apiEndpoints.message(workspaceId, threadId, messageId);
      const data = await authenticatedApi.put(endpoint, updateData);
      
      // Ensure the message has an id property
      const normalizedMessage = {
        ...data,
        id: getMessageId(data)
      };
      
      // Update current message if it's the one being edited
      if (currentMessage && getMessageId(currentMessage) === messageId) {
        setCurrentMessage(normalizedMessage);
      }
      
      // Update the message in the messages list
      setMessages(prevMessages => 
        prevMessages.map(message => 
          getMessageId(message) === messageId ? normalizedMessage : message
        )
      );
      
      setLoading(false);
      return normalizedMessage;
    } catch (err) {
      console.error('Error updating message:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  };

  /**
   * Deletes a message
   * @param {string} messageId - ID of the message to delete
   * @returns {Promise<boolean>} True if deleted successfully, false otherwise
   */
  const deleteMessage = async (messageId) => {
    if (!workspaceId || !threadId || !messageId) {
      setError(new Error('Workspace ID, Thread ID, and Message ID are required'));
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = apiEndpoints.message(workspaceId, threadId, messageId);
      await authenticatedApi.delete(endpoint);

      // If the current message is being deleted, clear it
      if (currentMessage && getMessageId(currentMessage) === messageId) {
        setCurrentMessage(null);
      }
      
      // Remove the message from the messages list
      setMessages(prevMessages => 
        prevMessages.filter(message => getMessageId(message) !== messageId)
      );
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error deleting message:', err);
      setError(err);
      setLoading(false);
      return false;
    }
  };

  /**
   * Deletes all messages in the thread
   * @returns {Promise<boolean>} True if deleted successfully, false otherwise
   */
  const deleteAllMessages = async () => {
    if (!workspaceId || !threadId) {
      setError(new Error('Workspace ID and Thread ID are required'));
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await authenticatedApi.delete(apiEndpoints.messages(workspaceId, threadId));

      // Clear all messages
      setMessages([]);
      setCurrentMessage(null);
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error deleting all messages:', err);
      setError(err);
      setLoading(false);
      return false;
    }
  };

  return {
    messages,
    currentMessage,
    loading,
    error,
    getMessages,
    getMessage,
    createMessage,
    updateMessage,
    deleteMessage,
    deleteAllMessages,
    setMessages, // Allow external setting of messages (for real-time updates)
  };
};

export default useMessages; 